import {
    formatFinancialTableCellValue,
    parseFinancialTableCellNumeric,
    rowRequiresTwoDecimalPlaces,
} from './financialGrowthChart'
import { BoardOfDirectorsDocument, parseBoardOfDirectorsDocument } from './boardOfDirectors'
import { getDisplayDate } from './dateTime'

export type StatementType = 'consolidated' | 'standalone'

export type FinancialPeriodMap = Record<string, Record<string, unknown>>

export type StatementDump = {
    consolidated?: FinancialPeriodMap
    standalone?: FinancialPeriodMap
}

export type FinancialDataDocument = {
    quarterlyOrder?: string[]
    quarterlyDataDump?: StatementDump
    annualOrder?: string[]
    annualDataDump?: StatementDump
    cashFlowOrder?: string[]
    cashFlowDataDump?: StatementDump
    balanceSheetOrder?: string[]
    balanceSheetDataDump?: StatementDump
    ratiosOrder?: string[]
    ratiosDataDump?: StatementDump
    isBanking?: boolean
    boardOfDirectors?: BoardOfDirectorsDocument
}

export type FinancialTableSection = {
    id: string
    label: string
    orderKey: keyof FinancialDataDocument
    dumpKey: keyof FinancialDataDocument
}

export type ResultsPeriodType = 'annual' | 'quarterly'

export const resolveResultsTableSection = (periodType: ResultsPeriodType): FinancialTableSection => ({
    id: 'results',
    label: 'Results',
    orderKey: periodType === 'annual' ? 'annualOrder' : 'quarterlyOrder',
    dumpKey: periodType === 'annual' ? 'annualDataDump' : 'quarterlyDataDump',
})

export const FINANCIAL_TABLE_SECTIONS: FinancialTableSection[] = [
    { id: 'cash_flow', label: 'Cash flow', orderKey: 'cashFlowOrder', dumpKey: 'cashFlowDataDump' },
    { id: 'balance_sheet', label: 'Balance sheet', orderKey: 'balanceSheetOrder', dumpKey: 'balanceSheetDataDump' },
    { id: 'ratios', label: 'Ratios', orderKey: 'ratiosOrder', dumpKey: 'ratiosDataDump' },
]

export type FinancialNavSection = {
    id: string
    label: string
}

export const FINANCIAL_SHAREHOLDING_SECTION_ID = 'shareholding'
export const FINANCIAL_DEALS_SECTION_ID = 'deals'
export const FINANCIAL_CORPORATE_ACTIONS_SECTION_ID = 'corporate_actions'
export const FINANCIAL_CREDIT_RATINGS_SECTION_ID = 'credit_ratings'

export const FINANCIAL_NAV_SECTIONS: FinancialNavSection[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'results', label: 'Results' },
    ...FINANCIAL_TABLE_SECTIONS.map(({ id, label }) => ({ id, label })),
    { id: FINANCIAL_SHAREHOLDING_SECTION_ID, label: 'Shareholding' },
    { id: FINANCIAL_DEALS_SECTION_ID, label: 'Deals' },
    { id: FINANCIAL_CORPORATE_ACTIONS_SECTION_ID, label: 'Corporate actions' },
    { id: FINANCIAL_CREDIT_RATINGS_SECTION_ID, label: 'Credit ratings' },
]

export const resolveFinancialNavSectionIndex = (sectionId: string | null | undefined): number => {
    if (!sectionId) {
        return 0
    }
    const index = FINANCIAL_NAV_SECTIONS.findIndex((section) => section.id === sectionId)
    return index >= 0 ? index : 0
}

export const resolveActiveTableSection = (
    tabIndex: number,
    resultsPeriodType: ResultsPeriodType,
): FinancialTableSection | undefined => {
    const navSection = FINANCIAL_NAV_SECTIONS[tabIndex]
    if (
        !navSection ||
        navSection.id === 'overview' ||
        navSection.id === FINANCIAL_SHAREHOLDING_SECTION_ID ||
        navSection.id === FINANCIAL_DEALS_SECTION_ID ||
        navSection.id === FINANCIAL_CORPORATE_ACTIONS_SECTION_ID ||
        navSection.id === FINANCIAL_CREDIT_RATINGS_SECTION_ID
    ) {
        return undefined
    }
    if (navSection.id === 'results') {
        return resolveResultsTableSection(resultsPeriodType)
    }
    return FINANCIAL_TABLE_SECTIONS.find((section) => section.id === navSection.id)
}

export const hasResultsPeriodData = (
    document: FinancialDataDocument,
    periodType: ResultsPeriodType,
): boolean => {
    const section = resolveResultsTableSection(periodType)
    const dump = document[section.dumpKey] as StatementDump | undefined
    const consolidated = getStatementDump(dump, 'consolidated')
    const standalone = getStatementDump(dump, 'standalone')
    return Object.keys(consolidated).length > 0 || Object.keys(standalone).length > 0
}

export type FinancialTableModel = {
    periods: string[]
    metrics: string[]
    rows: Record<string, Record<string, string>>
    rawRows: Record<string, Record<string, number | undefined>>
    statementType: StatementType
    hasConsolidated: boolean
    hasStandalone: boolean
}

const FINANCIAL_BODY_KEYS = new Set([
    'quarterlyOrder',
    'quarterlyDataDump',
    'annualOrder',
    'annualDataDump',
    'cashFlowOrder',
    'cashFlowDataDump',
    'balanceSheetOrder',
    'balanceSheetDataDump',
    'ratiosOrder',
    'ratiosDataDump',
    'isBanking',
])

const formatCellValue = (value: unknown, forceTwoDecimals = false): string =>
    formatFinancialTableCellValue(value, { forceTwoDecimals })

const hasFinancialBodyKeys = (value: Record<string, unknown>): boolean =>
    Object.keys(value).some((key) => FINANCIAL_BODY_KEYS.has(key))

const withBoardOfDirectors = (
    document: FinancialDataDocument,
    boardRaw: unknown,
): FinancialDataDocument => {
    const boardOfDirectors = parseBoardOfDirectorsDocument(boardRaw)
    if (!boardOfDirectors) {
        return document
    }
    return { ...document, boardOfDirectors }
}

export const normalizeFinancialDataDocument = (raw: unknown): FinancialDataDocument => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return {}
    }

    const record = raw as Record<string, unknown>
    const nestedBody = record.body
    if (nestedBody && typeof nestedBody === 'object' && !Array.isArray(nestedBody)) {
        const body = nestedBody as Record<string, unknown>
        return withBoardOfDirectors(body as FinancialDataDocument, body.boardOfDirectors ?? record.boardOfDirectors)
    }

    if (hasFinancialBodyKeys(record)) {
        return withBoardOfDirectors(record as FinancialDataDocument, record.boardOfDirectors)
    }

    const rawApiResponse = record.raw_api_response
    if (rawApiResponse && typeof rawApiResponse === 'object' && !Array.isArray(rawApiResponse)) {
        const apiBody = (rawApiResponse as Record<string, unknown>).body
        if (apiBody && typeof apiBody === 'object' && !Array.isArray(apiBody)) {
            const body = apiBody as Record<string, unknown>
            return withBoardOfDirectors(
                body as FinancialDataDocument,
                body.boardOfDirectors ?? record.boardOfDirectors,
            )
        }
    }

    const boardOnly = parseBoardOfDirectorsDocument(record.boardOfDirectors ?? record)
    if (boardOnly) {
        return { boardOfDirectors: boardOnly }
    }

    return {}
}

const getStatementDump = (dump: StatementDump | undefined, statementType: StatementType): FinancialPeriodMap =>
    dump?.[statementType] ?? {}

const periodHasMetrics = (periodData: Record<string, unknown> | undefined): boolean =>
    !!periodData && Object.keys(periodData).length > 0

export const resolveFinancialPeriods = (
    order: string[] | undefined,
    periodMap: FinancialPeriodMap,
): string[] => {
    const periodsFromOrder = (order ?? []).filter((period) => periodHasMetrics(periodMap[period]))
    if (periodsFromOrder.length > 0) {
        return periodsFromOrder
    }
    return Object.keys(periodMap).filter((period) => periodHasMetrics(periodMap[period]))
}

export const resolveLatestFinancialPeriods = (
    order: string[] | undefined,
    periodMap: FinancialPeriodMap,
    count: number,
): string[] => resolveFinancialPeriods(order, periodMap).slice(0, count)

export const resolveLatestFinancialPeriod = (
    order: string[] | undefined,
    periodMap: FinancialPeriodMap,
): string | undefined => resolveFinancialPeriods(order, periodMap)[0]

const countStatementPeriods = (periodMap: FinancialPeriodMap): number =>
    Object.keys(periodMap).filter((period) => periodHasMetrics(periodMap[period])).length

export const hasBothStatementTypes = (dump: StatementDump | undefined): boolean => {
    const consolidated = getStatementDump(dump, 'consolidated')
    const standalone = getStatementDump(dump, 'standalone')
    return countStatementPeriods(consolidated) > 0 && countStatementPeriods(standalone) > 0
}

export const resolveDefaultStatementType = (dump: StatementDump | undefined): StatementType => {
    const consolidated = getStatementDump(dump, 'consolidated')
    const standalone = getStatementDump(dump, 'standalone')
    const consolidatedCount = countStatementPeriods(consolidated)
    const standaloneCount = countStatementPeriods(standalone)

    if (consolidatedCount === 0 && standaloneCount === 0) {
        return 'consolidated'
    }
    if (consolidatedCount === 0) {
        return 'standalone'
    }
    if (standaloneCount === 0) {
        return 'consolidated'
    }
    return standaloneCount > consolidatedCount ? 'standalone' : 'consolidated'
}

export const buildFinancialTableModel = (
    document: FinancialDataDocument,
    section: FinancialTableSection,
    statementType: StatementType,
): FinancialTableModel => {
    const dump = document[section.dumpKey] as StatementDump | undefined
    const order = (document[section.orderKey] as string[] | undefined) ?? []
    const consolidated = getStatementDump(dump, 'consolidated')
    const standalone = getStatementDump(dump, 'standalone')
    const hasConsolidated = Object.keys(consolidated).length > 0
    const hasStandalone = Object.keys(standalone).length > 0
    const periodMap = statementType === 'consolidated' ? consolidated : standalone

    const periodsFromOrder = order.filter((period) => periodHasMetrics(periodMap[period]))
    const orderedPeriods = periodsFromOrder.length > 0 ? periodsFromOrder : Object.keys(periodMap)
    const periods = [...orderedPeriods].reverse()

    const metricSet = new Set<string>()
    periods.forEach((period) => {
        Object.entries(periodMap[period] ?? {}).forEach(([metric, value]) => {
            if (value !== null && typeof value !== 'object') {
                metricSet.add(metric)
            }
        })
    })

    const metrics = Array.from(metricSet)
    const rows: Record<string, Record<string, string>> = {}
    const rawRows: Record<string, Record<string, number | undefined>> = {}
    metrics.forEach((metric) => {
        rows[metric] = {}
        rawRows[metric] = {}
        const rawByPeriod: Record<string, unknown> = {}
        periods.forEach((period) => {
            const rawValue = periodMap[period]?.[metric]
            rawByPeriod[period] = rawValue
            rawRows[metric][period] = parseFinancialTableCellNumeric(rawValue)
        })
        const forceTwoDecimals = rowRequiresTwoDecimalPlaces(
            periods.map((period) => rawRows[metric][period]),
        )
        periods.forEach((period) => {
            rows[metric][period] = formatCellValue(rawByPeriod[period], forceTwoDecimals)
        })
    })

    return {
        periods,
        metrics,
        rows,
        rawRows,
        statementType,
        hasConsolidated,
        hasStandalone,
    }
}

const QUARTERLY_EPS_METRIC = 'EPS'

export const extractLatestQuarterlyEps = (document: FinancialDataDocument): string | undefined => {
    const dump = document.quarterlyDataDump
    if (!dump) {
        return undefined
    }
    const statementType = resolveDefaultStatementType(dump)
    const periodMap = getStatementDump(dump, statementType)
    const order = document.quarterlyOrder ?? []
    const latestPeriod =
        order.find((period) => periodHasMetrics(periodMap[period])) ?? Object.keys(periodMap).find((p) => periodHasMetrics(periodMap[p]))
    if (!latestPeriod) {
        return undefined
    }
    const eps = periodMap[latestPeriod]?.[QUARTERLY_EPS_METRIC]
    const formatted = formatCellValue(eps)
    return formatted || undefined
}

export const isFinancialDataEmpty = (document: FinancialDataDocument | null | undefined): boolean => {
    if (!document) return true

    const isSectionEmpty = (section: FinancialTableSection): boolean => {
        const dump = document[section.dumpKey] as StatementDump | undefined
        const consolidated = getStatementDump(dump, 'consolidated')
        const standalone = getStatementDump(dump, 'standalone')
        return Object.keys(consolidated).length === 0 && Object.keys(standalone).length === 0
    }

    const resultsEmpty =
        isSectionEmpty(resolveResultsTableSection('annual')) &&
        isSectionEmpty(resolveResultsTableSection('quarterly'))

    return resultsEmpty && FINANCIAL_TABLE_SECTIONS.every(isSectionEmpty)
}

export const parseFinancialDataResponse = (raw: unknown): FinancialDataDocument => {
    if (typeof raw === 'string') {
        if (!raw.trim()) {
            return {}
        }
        try {
            return normalizeFinancialDataDocument(JSON.parse(raw))
        } catch {
            return {}
        }
    }
    return normalizeFinancialDataDocument(raw)
}

export type CreditRatingRow = {
    date: string
    agency: string
    description: string
    ctaType: string
    url: string
    creditRating: string
    outlook: string
    ratingAction: string
    source: string
}

const asCreditRatingString = (value: unknown): string => {
    if (typeof value === 'string') {
        return value.trim()
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value)
    }
    return ''
}

const parseCreditRatingRow = (raw: unknown): CreditRatingRow | undefined => {
    if (!raw || typeof raw !== 'object') {
        return undefined
    }
    const row = raw as Record<string, unknown>
    return {
        date: asCreditRatingString(row.date),
        agency: asCreditRatingString(row.agency),
        description: asCreditRatingString(row.description),
        ctaType: asCreditRatingString(row.cta_type ?? row.ctaType),
        url: asCreditRatingString(row.url),
        creditRating: asCreditRatingString(row.credit_rating ?? row.creditRating),
        outlook: asCreditRatingString(row.outlook),
        ratingAction: asCreditRatingString(row.rating_action ?? row.ratingAction),
        source: asCreditRatingString(row.source),
    }
}

export const parseCreditRatingsDocument = (raw: unknown): { ratings: CreditRatingRow[] } => {
    if (!raw || typeof raw !== 'object') {
        return { ratings: [] }
    }
    const doc = raw as Record<string, unknown>
    const ratings = Array.isArray(doc.ratings)
        ? doc.ratings.map(parseCreditRatingRow).filter((row): row is CreditRatingRow => Boolean(row))
        : []
    ratings.sort((a, b) => (a.date === b.date ? 0 : a.date < b.date ? 1 : -1))
    return { ratings }
}

export const getCreditRatingRowKey = (row: CreditRatingRow, index: number): string =>
    [row.date, row.agency, row.creditRating, row.url, row.source, String(index)].join('|')

export const formatCreditRatingValue = (rating: string): string =>
    rating
        .replace(/\[([^\]]+)]\s*/g, '$1 ')
        .replace(/\s+/g, ' ')
        .trim()

export const formatCreditRatingAgency = (agency: string): string => {
    const trimmed = agency.trim()
    if (!trimmed) {
        return ''
    }
    if (trimmed.toUpperCase().includes('INDIA RATING')) {
        return 'INDIA RATINGS'
    }
    return trimmed.split(/\s+/)[0]
}

export const formatCreditRatingDate = (isoDate: string): string => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim())
    if (!match) {
        return isoDate.trim() || '—'
    }
    return getDisplayDate(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getTime())
}
